import { deliveryMethods } from "@/config/storeConfig";
import { formatBRL } from "@/lib/format";
import type { DeliveryMethodId } from "@/types";

interface DeliveryOptionsProps {
  value: DeliveryMethodId | "";
  onChange: (id: DeliveryMethodId) => void;
  error?: string;
}

export default function DeliveryOptions({ value, onChange, error }: DeliveryOptionsProps) {
  return (
    <div className="flex flex-col gap-3">
      {deliveryMethods.map((method) => {
        const active = value === method.id;
        const disabled = !method.available;
        return (
          <button
            key={method.id}
            type="button"
            disabled={disabled}
            aria-pressed={active}
            onClick={() => onChange(method.id)}
            className={`flex w-full cursor-pointer flex-col gap-2 rounded-lg border p-4 text-left transition-colors ${
              disabled
                ? "cursor-not-allowed border-background-200 bg-background-100 opacity-60"
                : active
                  ? "border-primary-500 bg-primary-50"
                  : "border-background-300 bg-background-50 hover:border-primary-300"
            }`}
          >
            <div className="flex w-full items-start justify-between gap-3">
              <div className="flex items-start gap-3">
                <span
                  className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border ${
                    active ? "border-primary-500 bg-primary-500 text-background-50" : "border-background-400"
                  }`}
                >
                  {active ? <i className="ri-check-line text-xs" /> : null}
                </span>
                <div>
                  <p className="font-heading text-lg leading-tight text-foreground-900">{method.name}</p>
                  <p className="mt-1 text-xs leading-relaxed text-foreground-600">{method.description}</p>
                </div>
              </div>
              <span className="shrink-0 whitespace-nowrap rounded-full bg-secondary-100 px-3 py-1 text-xs font-medium text-secondary-900">
                {method.fee === null ? "Taxa a confirmar" : method.fee === 0 ? "Sem taxa" : formatBRL(method.fee)}
              </span>
            </div>

            <div className="flex flex-wrap gap-x-4 gap-y-1 pl-8 text-[11px] text-foreground-500">
              <span className="flex items-center gap-1.5">
                <i className="ri-time-line text-sm" />
                {method.deadline}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-map-pin-line text-sm" />
                {method.regions}
              </span>
            </div>

            {method.notice ? (
              <p className="pl-8 text-[11px] italic text-foreground-500">{method.notice}</p>
            ) : null}
          </button>
        );
      })}

      {error ? (
        <p className="flex items-center gap-2 rounded-md border border-accent-200 bg-accent-50 px-3 py-2 text-xs text-accent-900">
          <i className="ri-error-warning-line text-base" />
          {error}
        </p>
      ) : null}

      <p className="rounded-lg border border-secondary-200 bg-secondary-50 p-3 text-[11px] leading-relaxed text-foreground-700">
        As taxas e os prazos são demonstrativos. O valor e o prazo definitivos serão confirmados no
        atendimento.
      </p>
    </div>
  );
}